import { defineComponent as _defineComponent } from 'vue'
import { computed, onActivated, onDeactivated, ref, watch } from "vue";

import store from "@/store";
import { WebcamFlip } from "@/store/settings";


export default /*#__PURE__*/_defineComponent({
  __name: 'WebcamPanel',
  setup(__props) {

const webcam = computed(() => store.state.settings.webcam);
const webcamIsRTC = computed(() => webcam.value.url.startsWith("ws:") || webcam.value.url.startsWith("wss:"));

// Lifecycle
const active = ref(true);

onActivated(() => {
	active.value = true;
	if (!webcam.value.embedded) {
		update();
		if (!webcamIsRTC.value && webcam.value.updateInterval > 0) {
			updateTimer = setInterval(update, webcam.value.updateInterval);
		}
	}
});

onDeactivated(() => {
	closeWebRTC();
	if (updateTimer !== null) {
		clearInterval(updateTimer);
		updateTimer = null;
	}
	active.value = false;
});

// WebRTC
const remoteVideo = ref<HTMLVideoElement | null>(null);
let signalingSocket: WebSocket | null = null, peerConnection: RTCPeerConnection | null = null;

let connectingRTC = false;
async function connectWebRTC() {
	// Don't connect if we're already trying to connect
	if (connectingRTC) {
		return;
	}
	connectingRTC = true;

	// Create RTC peer connection
	peerConnection = new RTCPeerConnection({
		//iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
	});

	if (remoteVideo.value !== null) {
		const audioTrack = peerConnection!.addTransceiver("audio", { direction: "recvonly" }).receiver.track;
		const videoTrack = peerConnection!.addTransceiver("video", { direction: "recvonly" }).receiver.track;
		remoteVideo.value.srcObject = new MediaStream([audioTrack, videoTrack]);
	}

	// Make signaling socket
	signalingSocket = new WebSocket(url.value);
	signalingSocket.onerror = () => {
		connectingRTC = false;
	};
	signalingSocket.onopen = () => {
		connectingRTC = false;

		peerConnection!.addEventListener("icecandidate", e => {
			if (e.candidate) {
				const msg = { type: "webrtc/candidate", value: e.candidate.candidate };
				signalingSocket!.send(JSON.stringify(msg));
			}
		});

		peerConnection!.createOffer().then(offer => peerConnection!.setLocalDescription(offer)).then(() => {
			const msg = { type: "webrtc/offer", value: peerConnection!.localDescription?.sdp };
			signalingSocket!.send(JSON.stringify(msg));
		});
	}
	signalingSocket.onmessage = (e) => {
		const msg = JSON.parse(e.data);
		if (msg.type === "webrtc/candidate") {
			peerConnection!.addIceCandidate({ candidate: msg.value, sdpMid: "0" });
		} else if (msg.type === "webrtc/answer") {
			peerConnection!.setRemoteDescription({ type: "answer", sdp: msg.value });
		}
	}
}

function closeWebRTC() {
	if (peerConnection !== null) {
		peerConnection.close();
		peerConnection = null;
	}

	if (signalingSocket !== null) {
		signalingSocket.close();
		signalingSocket = null;
	}
}

// Update mechanism
const url = ref("");
let updateTimer: ReturnType<typeof setInterval> | null = null;

function update() {
	let urlValue = webcam.value.url.replace("[HOSTNAME]", store.getters["machine/connector"] ? store.getters["machine/connector"].hostname : location.hostname);
	if (webcamIsRTC.value) {
		// Kill existing WebRTC connections
		closeWebRTC();

		// Try to connect again
		url.value = urlValue;
		connectWebRTC();
	} else if (webcam.value.updateInterval > 0) {
		// Generate webcam URL
		if (webcam.value.useFix) {
			urlValue += "_" + Math.random();
		} else {
			if (urlValue.indexOf("?") === -1) {
				urlValue += "?dummy=" + Math.random();
			} else {
				urlValue += "&dummy=" + Math.random();
			}
		}

		// Apply it
		url.value = urlValue;
	} else {
		// Use URL as specified in the settings
		url.value = webcam.value.url;
	}
}

watch(() => webcam.value, () => {
	if (webcam.value.updateInterval === 0) {
		// For persistent images or RTC streams we need to apply updates independently of the update loop
		update();
	}
}, { deep: true });

// Styling
const classList = computed(() => {
	const result = [];

	if (webcam.value.flip === WebcamFlip.X || webcam.value.flip === WebcamFlip.Both) {
		result.push("flip-x");
	}
	if (webcam.value.flip === WebcamFlip.Y || webcam.value.flip === WebcamFlip.Both) {
		result.push("flip-y");
	}

	if (webcam.value.rotation === 90) {
		result.push("rotate-90");
	} else if (webcam.value.rotation === 180) {
		result.push("rotate-180");
	} else if (webcam.value.rotation === 270) {
		result.push("rotate-270");
	}

	return result;
});

return { __sfc: true,webcam, webcamIsRTC, active, remoteVideo, signalingSocket, peerConnection, connectingRTC, connectWebRTC, closeWebRTC, url, updateTimer, update, classList }
}

})